// src/feature-module/hotel/hotel-details/HotelGuestDetails.tsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import API_URLS from "../../../core/common/apiUrls";
import { all_routes } from "../../router/all_routes";
import OfferPopup, { AppliedCoupon } from "../../../core/common/OfferPopup";
import "../../../core/common/OfferPopup.css";

declare global {
  interface Window {
    Razorpay: any;
  }
}

/** ----------------- Types ----------------- */
interface GuestFormData {
  Title: string;
  FirstName: string;
  LastName: string;
  Gender: number; // 1=Male,2=Female,3=Other
  DateOfBirth: string; // YYYY-MM-DD
  Age?: number | null;
  Nationality: string;
  ContactNo: string;
  Email: string;
  IsLeadGuest: boolean;
  IsChild?: boolean;
  PanCard?: string;
  Passport?: string;
}

/* ----------------- Session helpers ----------------- */
const rehydrateSessionFromLocal = () => {
  try {
    const ssUserId = sessionStorage.getItem("user_id");
    if (!ssUserId) {
      const lsUserRaw = localStorage.getItem("user");
      if (lsUserRaw) {
        const lsUser = JSON.parse(lsUserRaw);
        if (lsUser?.id != null) {
          sessionStorage.setItem("user_id", String(lsUser.id));
          sessionStorage.setItem("user", JSON.stringify(lsUser));
        }
      }
    }
  } catch {}
};

const getCurrentUserId = (): number | null => {
  const ssId = sessionStorage.getItem("user_id");
  if (ssId) return Number(ssId);

  try {
    const ssUser = JSON.parse(sessionStorage.getItem("user") || "null");
    if (ssUser?.id != null) return Number(ssUser.id);
  } catch {}

  try {
    const lsUser = JSON.parse(localStorage.getItem("user") || "null");
    if (lsUser?.id != null) {
      sessionStorage.setItem("user_id", String(lsUser.id));
      sessionStorage.setItem("user", JSON.stringify(lsUser));
      return Number(lsUser.id);
    }
  } catch {}

  return null;
};

/* ----------------- Validation helpers ----------------- */
const isNonEmpty = (v?: string) => !!v && String(v).trim().length > 0;
const isValidEmail = (email?: string) => {
  if (!email) return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).trim());
};
const normalizePhone = (raw?: string) => (raw || "").toString().replace(/\D/g, "");
const isValidPhone = (raw?: string, country?: string) => {
  const pn = normalizePhone(raw);
  if (!pn) return false;
  if (country === "IN") {
    return /^[6-9]\d{9}$/.test(pn);
  }
  return pn.length >= 7 && pn.length <= 15;
};
const isValidPAN = (pan?: string) => {
  if (!pan) return false;
  return /^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(String(pan).trim());
};
const isValidGSTIN = (gst?: string) => {
  if (!gst) return false;
  return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/i.test(String(gst).trim());
};
const isValidPassport = (p?: string) => {
  if (!p) return false;
  const s = String(p).trim();
  return /^[A-Za-z0-9]{6,9}$/.test(s);
};

/* ----------------- Component ----------------- */
const HotelGuestDetails: React.FC = () => {
  const routes = all_routes;
  const location = useLocation();
  const navigate = useNavigate();

  const hotelDetails: any = location.state?.hotelDetails;
  const prebookData: any = location.state?.prebookData;
  const paxRooms: any[] =
    (location.state?.paxRooms as any[]) ||
    (location.state?.PaxRooms as any[]) ||
    [{ Adults: 2, Children: 0, ChildrenAges: [] }];

  const firstRoom: any = prebookData?.HotelResult?.[0]?.Rooms?.[0] || null;

  const [guests, setGuests] = useState<GuestFormData[]>([]);
  const [validationInfo, setValidationInfo] = useState<any>(null);

  // per-field inline errors: key = `${index}.${field}`
  const [formFieldErrors, setFormFieldErrors] = useState<Record<string, string>>({});

  const [savingGuests, setSavingGuests] = useState<boolean>(false);
  const [bookingInProgress, setBookingInProgress] = useState<boolean>(false);
  const [bookingMessage, setBookingMessage] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const [gstEnabled, setGstEnabled] = useState<boolean>(false);
  const [gstNumber, setGstNumber] = useState<string>("");
  const [showPaymentCancelledModal, setShowPaymentCancelledModal] = useState<boolean>(false);

  // Offer modal visibility
  const [offersVisible, setOffersVisible] = useState<boolean>(false);
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);

  useEffect(() => {
    rehydrateSessionFromLocal();
  }, []);

  useEffect(() => {
    if (!prebookData || !hotelDetails || !firstRoom) {
      navigate(-1);
      return;
    }

    setValidationInfo(
      prebookData.ValidationInfo ||
        prebookData?.HotelResult?.[0]?.ValidationInfo ||
        {}
    );

    // Build initial guests array using paxRooms and any provided ChildrenAges
    const initialGuests: GuestFormData[] = [];
    let globalChildIndex = 0;
    paxRooms.forEach((r: any) => {
      const adults = Number(r.Adults || 0);
      const children = Number(r.Children || 0);
      const childAgesArr = Array.isArray(r.ChildrenAges) ? r.ChildrenAges : [];

      for (let a = 0; a < adults; a++) {
        initialGuests.push({
          Title: "Mr",
          FirstName: "",
          LastName: "",
          Gender: 1,
          DateOfBirth: "1990-01-01",
          Age: null,
          Nationality: "IN",
          ContactNo: "",
          Email: "",
          IsLeadGuest: initialGuests.length === 0,
          IsChild: false,
          PanCard: "",
          Passport: "",
        });
      }

      for (let c = 0; c < children; c++) {
        const suppliedAge = childAgesArr[c] ?? childAgesArr[globalChildIndex] ?? null;
        let dob = "";
        let ageVal: number | null = null;
        if (suppliedAge != null && suppliedAge !== "") {
          ageVal = Number(suppliedAge);
          if (!Number.isNaN(ageVal)) {
            dob = dayjs().subtract(ageVal, "year").format("YYYY-MM-DD");
          } else {
            ageVal = null;
            dob = "";
          }
        }
        initialGuests.push({
          Title: "Master",
          FirstName: "",
          LastName: "",
          Gender: 1,
          DateOfBirth: dob,
          Age: ageVal,
          Nationality: "IN",
          ContactNo: "",
          Email: "",
          IsLeadGuest: false,
          IsChild: true,
          PanCard: "",
          Passport: "",
        });
        globalChildIndex++;
      }
    });

    if (initialGuests.length === 0) {
      initialGuests.push({
        Title: "Mr",
        FirstName: "",
        LastName: "",
        Gender: 1,
        DateOfBirth: "1990-01-01",
        Age: null,
        Nationality: "IN",
        ContactNo: "",
        Email: "",
        IsLeadGuest: true,
        IsChild: false,
        PanCard: "",
        Passport: "",
      });
    }

    setGuests(initialGuests);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prebookData, hotelDetails, navigate]);

  const clearFieldError = (index: number, field: keyof GuestFormData) => {
    const key = `${index}.${String(field)}`;
    setFormFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const setFieldError = (index: number, field: string, msg: string | null) => {
    const key = `${index}.${field}`;
    setFormFieldErrors((prev) => {
      const copy = { ...prev };
      if (!msg) delete copy[key];
      else copy[key] = msg;
      return copy;
    });
  };

  const validateField = (index: number, field: keyof GuestFormData) => {
    const g = guests[index];
    const pos = `Guest ${index + 1}`;

    if (!g) return;

    switch (field) {
      case "FirstName":
        if (!isNonEmpty(g.FirstName)) setFieldError(index, "FirstName", "First name is required.");
        else setFieldError(index, "FirstName", null);
        break;
      case "LastName":
        if (!isNonEmpty(g.LastName)) setFieldError(index, "LastName", "Last name is required.");
        else setFieldError(index, "LastName", null);
        break;
      case "DateOfBirth": {
        if (g.IsChild) {
          // child can have DOB or Age
          if (!isNonEmpty(g.DateOfBirth) && (g.Age == null || Number.isNaN(Number(g.Age)))) {
            setFieldError(index, "DateOfBirth", "Enter DOB or Age for child.");
          } else if (isNonEmpty(g.DateOfBirth)) {
            const birth = dayjs(g.DateOfBirth, "YYYY-MM-DD", true);
            if (!birth.isValid()) setFieldError(index, "DateOfBirth", "DOB must be a valid date (YYYY-MM-DD).");
            else if (birth.isAfter(dayjs())) setFieldError(index, "DateOfBirth", "DOB cannot be in the future.");
            else setFieldError(index, "DateOfBirth", null);
          } else setFieldError(index, "DateOfBirth", null);
        } else {
          if (!isNonEmpty(g.DateOfBirth)) setFieldError(index, "DateOfBirth", "Date of birth is required for adults.");
          else {
            const birth = dayjs(g.DateOfBirth, "YYYY-MM-DD", true);
            if (!birth.isValid()) setFieldError(index, "DateOfBirth", "DOB must be a valid date (YYYY-MM-DD).");
            else if (birth.isAfter(dayjs())) setFieldError(index, "DateOfBirth", "DOB cannot be in the future.");
            else setFieldError(index, "DateOfBirth", null);
          }
        }
        break;
      }
      case "Age": {
        if (g.IsChild) {
          const a = Number(g.Age);
          if (g.Age == null || Number.isNaN(a)) setFieldError(index, "Age", "Enter age for child or provide DOB.");
          else if (a < 0 || a > 17) setFieldError(index, "Age", "Age must be between 0 and 17 for children.");
          else setFieldError(index, "Age", null);
        } else {
          setFieldError(index, "Age", null);
        }
        break;
      }
      case "PanCard": {
        if (!g.IsChild && validationInfo?.PanMandatory && !isNonEmpty(g.PanCard)) {
          setFieldError(index, "PanCard", "PAN is required for this hotel.");
        } else if (!g.IsChild && isNonEmpty(g.PanCard) && !isValidPAN(g.PanCard)) {
          setFieldError(index, "PanCard", "PAN format looks invalid.");
        } else setFieldError(index, "PanCard", null);
        break;
      }
      case "Passport": {
        if (validationInfo?.PassportMandatory && !isNonEmpty(g.Passport)) setFieldError(index, "Passport", "Passport number is required.");
        else if (isNonEmpty(g.Passport) && !isValidPassport(g.Passport)) setFieldError(index, "Passport", "Passport format looks invalid.");
        else setFieldError(index, "Passport", null);
        break;
      }
      case "Nationality": {
        if (!isNonEmpty(g.Nationality)) setFieldError(index, "Nationality", "Nationality is required.");
        else setFieldError(index, "Nationality", null);
        break;
      }
      case "ContactNo": {
        // only validate contact when this guest is lead
        if (g.IsLeadGuest) {
          if (!isNonEmpty(g.ContactNo)) setFieldError(index, "ContactNo", "Contact number is required.");
          else {
            const country = (hotelDetails?.CountryCode || hotelDetails?.Country || "IN").toUpperCase();
            if (!isValidPhone(g.ContactNo, country === "IN" ? "IN" : undefined)) setFieldError(index, "ContactNo", "Contact number looks invalid.");
            else setFieldError(index, "ContactNo", null);
          }
        } else setFieldError(index, "ContactNo", null);
        break;
      }
      case "Email": {
        if (g.IsLeadGuest) {
          if (!isNonEmpty(g.Email)) setFieldError(index, "Email", "Email is required.");
          else if (!isValidEmail(g.Email)) setFieldError(index, "Email", "Email looks invalid.");
          else setFieldError(index, "Email", null);
        } else setFieldError(index, "Email", null);
        break;
      }
      default:
        break;
    }
  };

  const handleGuestChange = (
    index: number,
    field: keyof GuestFormData,
    value: any
  ) => {
    setGuests((prev) =>
      prev.map((g, i) => {
        if (i !== index) return g;
        const updated = { ...g, [field]: value };

        // DOB <-> Age sync
        if (field === "DateOfBirth") {
          if (value) {
            const birth = dayjs(value, "YYYY-MM-DD", true);
            if (birth.isValid()) {
              const age = dayjs().diff(birth, "year");
              updated.Age = Number.isNaN(Number(age)) ? null : age;
            } else {
              updated.Age = null;
            }
          } else {
            updated.Age = null;
          }
        } else if (field === "Age") {
          const aNum = Number(value);
          if (!Number.isNaN(aNum) && aNum >= 0) {
            updated.DateOfBirth = dayjs().subtract(aNum, "year").format("YYYY-MM-DD");
            updated.Age = aNum;
          } else {
            updated.DateOfBirth = "";
            updated.Age = null;
          }
        }

        if (field === "IsChild") {
          if (value) updated.Title = updated.Title && updated.Title.toLowerCase().includes("master") ? updated.Title : "Master";
          else if (!value && updated.Title === "Master") updated.Title = "Mr";
        }

        return updated;
      })
    );

    // clear inline error for this field while typing
    clearFieldError(index, field);
  };

  const calcAgeFromDOB = (dob: string) => {
    try {
      if (!dob) return null;
      const birth = dayjs(dob, "YYYY-MM-DD", true);
      if (!birth.isValid()) return null;
      return dayjs().diff(birth, "year");
    } catch {
      return null;
    }
  };

  const buildHotelRoomsDetails = (guestList: GuestFormData[], paxRoomsLocal: any[]) => {
    const roomsDetails: any[] = [];
    let cursor = 0;
    for (let ri = 0; ri < paxRoomsLocal.length; ri++) {
      const room = paxRoomsLocal[ri];
      const peopleInRoom = Number(room.Adults || 0) + Number(room.Children || 0);
      const passengers: any[] = [];

      for (let p = 0; p < peopleInRoom; p++) {
        const g: GuestFormData | undefined = guestList[cursor];
        if (!g) {
          passengers.push({
            Title: "Mr",
            FirstName: "Guest",
            LastName: `${cursor + 1}`,
            Phoneno: "",
            Email: "",
            PaxType: p < (room.Adults || 0) ? 1 : 2,
            LeadPassenger: p === 0,
            Age: 30,
          });
        } else {
          passengers.push({
            Title: g.Title || "Mr",
            FirstName: g.FirstName || "Guest",
            LastName: g.LastName || `${cursor + 1}`,
            Phoneno: g.ContactNo || "",
            Email: g.Email || "",
            PaxType: 1,
            LeadPassenger: !!g.IsLeadGuest,
            Age: calcAgeFromDOB(g.DateOfBirth || "") || (g.Age || 0),
          });
        }
        cursor++;
      }
      const adultsCount = Number(room.Adults || 0);
      for (let i = 0; i < passengers.length; i++) {
        passengers[i].PaxType = i < adultsCount ? 1 : 2;
      }
      if (!passengers.some((p) => p.LeadPassenger)) passengers[0].LeadPassenger = true;

      roomsDetails.push({ HotelPassenger: passengers });
    }
    return roomsDetails;
  };

  /** Validation (submit-time) */
  const validateGuests = (): { ok: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!guests || guests.length === 0) {
      errors.push("No guest details provided.");
      return { ok: false, errors };
    }

    guests.forEach((g, idx) => {
      const pos = `Guest ${idx + 1}`;
      if (!isNonEmpty(g.FirstName)) errors.push(`${pos}: First name is required.`);
      if (!isNonEmpty(g.LastName)) errors.push(`${pos}: Last name is required.`);

      if (g.IsChild) {
        const hasDOB = !!g.DateOfBirth && String(g.DateOfBirth).trim().length > 0;
        const hasAge = typeof g.Age === "number" && !Number.isNaN(g.Age);
        if (!hasDOB && !hasAge) {
          errors.push(`${pos}: Age or Date of Birth is required for children.`);
        } else {
          if (hasDOB) {
            const birth = dayjs(g.DateOfBirth, "YYYY-MM-DD", true);
            if (!birth.isValid()) errors.push(`${pos}: Date of birth must be a valid date (YYYY-MM-DD).`);
            else if (birth.isAfter(dayjs())) errors.push(`${pos}: Date of birth cannot be in the future.`);
          }
          if (hasAge) {
            const a = Number(g.Age);
            if (Number.isNaN(a) || a < 0 || a > 17) {
              errors.push(`${pos}: Age for child must be a number between 0 and 17.`);
            }
          }
        }
      } else {
        if (!g.DateOfBirth) errors.push(`${pos}: Date of birth is required for adults.`);
        else {
          const birth = dayjs(g.DateOfBirth, "YYYY-MM-DD", true);
          if (!birth.isValid()) errors.push(`${pos}: Date of birth must be a valid date (YYYY-MM-DD).`);
          else if (birth.isAfter(dayjs())) errors.push(`${pos}: Date of birth cannot be in the future.`);
        }
      }

      if (validationInfo?.PassportMandatory && !isNonEmpty(g.Passport)) {
        errors.push(`${pos}: Passport number is required for this hotel.`);
      } else if (isNonEmpty(g.Passport) && !isValidPassport(g.Passport)) {
        errors.push(`${pos}: Passport format looks invalid.`);
      }

      if (validationInfo?.PanMandatory && !g.IsChild && !isNonEmpty(g.PanCard)) {
        errors.push(`${pos}: PAN is required for this hotel.`);
      } else if (!g.IsChild && isNonEmpty(g.PanCard) && !isValidPAN(g.PanCard)) {
        errors.push(`${pos}: PAN format looks invalid.`);
      }
    });

    const lead = guests.find((x) => x.IsLeadGuest) || guests[0];
    if (!isNonEmpty(lead.ContactNo?.toString())) {
      errors.push("Lead guest contact number is required.");
    } else {
      const country = (hotelDetails?.CountryCode || hotelDetails?.Country || "IN").toUpperCase();
      if (!isValidPhone(lead.ContactNo?.toString(), country === "IN" ? "IN" : undefined)) {
        errors.push("Lead guest contact number looks invalid.");
      }
    }

    if (!isNonEmpty(lead.Email?.toString())) {
      errors.push("Lead guest email is required.");
    } else {
      if (!isValidEmail(lead.Email)) errors.push("Lead guest email looks invalid.");
    }

    if (validationInfo?.GSTAllowed && gstEnabled) {
      if (!isNonEmpty(gstNumber) || !isValidGSTIN(gstNumber)) {
        errors.push("Please enter a valid GSTIN.");
      }
    }

    if (validationInfo?.PanCountRequired) {
      const required = Number(validationInfo.PanCountRequired || 0);
      const panProvidedCount = guests.reduce((acc, g) => {
        const isChild = !!g.IsChild;
        const hasPan = isNonEmpty(g.PanCard);
        return acc + (isChild ? 0 : (hasPan ? 1 : 0));
      }, 0);

      if (panProvidedCount < required) {
        errors.push(`At least ${required} guest PAN${required > 1 ? "s" : ""} are required (PAN typically not required for children).`);
      }
    }

    return { ok: errors.length === 0, errors };
  };

  /** MAIN FLOW: Save guests → create order → open Razorpay → verify → BOOK */
  const handleSaveGuests = async () => {
    if (!firstRoom) return;

    const userId = getCurrentUserId();
    if (!userId) {
      alert("Please log in to continue.");
      return;
    }

    setFormErrors([]);
    setPaymentError(null);

    // run submit-time validation
    const v = validateGuests();
    if (!v.ok) {
      setFormErrors(v.errors);
      // also map individual field errors where possible
      guests.forEach((g, idx) => {
        if (!isNonEmpty(g.FirstName)) setFieldError(idx, "FirstName", "First name is required.");
        if (!isNonEmpty(g.LastName)) setFieldError(idx, "LastName", "Last name is required.");
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSavingGuests(true);
    try {
      const leadGuest = guests.find((g) => g.IsLeadGuest) || guests[0];

      const hotelOfferId =
        prebookData?.hotel_offer_id ||
        prebookData?.HotelOfferId ||
        prebookData?.hotelOfferId;
      if (!hotelOfferId) {
        setFormErrors(["HotelOfferId missing. Please retry booking."]);
        setSavingGuests(false);
        return;
      }

      // Ensure every guest has DateOfBirth for backend:
      const guestsForSave = guests.map((g) => {
        const copy: any = { ...g };

        if (copy.IsChild && (!copy.DateOfBirth || String(copy.DateOfBirth).trim() === "") && (typeof copy.Age === "number" && !Number.isNaN(copy.Age))) {
          const aNum = Number(copy.Age);
          if (!Number.isNaN(aNum)) {
            copy.DateOfBirth = dayjs().subtract(aNum, "year").format("YYYY-MM-DD");
          }
        }

        if (copy.DateOfBirth && String(copy.DateOfBirth).trim().length > 0) {
          const calc = calcAgeFromDOB(copy.DateOfBirth);
          copy.Age = calc ?? copy.Age ?? null;
        }

        return copy;
      });

      // 1) SAVE GUESTS
      const savePayload = {
        HotelOfferId: hotelOfferId,
        UserId: userId,
        Guests: guestsForSave.map((g: any) => ({
          Title: g.Title,
          FirstName: g.FirstName,
          LastName: g.LastName,
          Gender: g.Gender === 1 ? "Male" : g.Gender === 2 ? "Female" : "Other",
          DateOfBirth: g.DateOfBirth || "",
          Age: typeof g.Age === "number" ? Number(g.Age) : undefined,
          ContactNo: leadGuest.ContactNo,
          Email: leadGuest.Email,
          IsLeadGuest: g.IsLeadGuest,
          City: hotelDetails?.City || hotelDetails?.CityName || "Mumbai",
          Nationality: g.Nationality,
          PanNumber: g.IsChild ? "" : (g.PanCard || ""),
          PassportNumber: g.Passport || "",
          IsChild: !!g.IsChild,
        })),
      };

      const saveRes = await fetch(API_URLS.HOTELS.SAVE_GUESTS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(savePayload),
      });

      if (!saveRes.ok) {
        let serverMsg = "Failed to save guest details. Please try again.";
        try {
          const j = await saveRes.json();
          serverMsg = j?.error || j?.message || serverMsg;
        } catch {
          try {
            const txt = await saveRes.text();
            if (txt) serverMsg = txt;
          } catch {}
        }
        setFormErrors([serverMsg]);
        setSavingGuests(false);
        return;
      }

      const guestRes = await saveRes.json();
      const tempId = guestRes?.TempId || guestRes?.tempId;
      if (!tempId) {
        setFormErrors(["Unable to save guest details. Please contact support."]);
        setSavingGuests(false);
        return;
      }

      // 2) CREATE PAYMENT ORDER
      const netAmount =
        prebookData?.HotelResult?.[0]?.Rooms?.[0]?.NetAmount ||
        prebookData?.HotelResult?.[0]?.Rooms?.[0]?.TotalFare ||
        firstRoom.TotalFare ||
        0;

      const payableAmount = appliedCoupon
        ? appliedCoupon.final_amount
        : Math.round(Number(netAmount) * 100) / 100;

      const orderRes = await fetch(API_URLS.PAYMENTS.CREATE_ORDER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: payableAmount,
          currency: prebookData?.HotelResult?.[0]?.Currency || "INR",
          coupon_code: appliedCoupon?.code || null,
          discount_amount: appliedCoupon?.discount_amount || 0,
        }),
      });

      if (!orderRes.ok) {
        let serverMsg = "Failed to create payment order. Please try again.";
        try {
          const j = await orderRes.json();
          serverMsg = j?.error || j?.message || serverMsg;
        } catch {}
        setFormErrors([serverMsg]);
        setSavingGuests(false);
        return;
      }

      const orderData = await orderRes.json();

      // 3) OPEN RAZORPAY
      const lead = guests.find((g) => g.IsLeadGuest) || guests[0];
      const options: any = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: hotelDetails?.HotelName || "Hotel Booking",
        description: "Room Booking Payment",
        order_id: orderData.order_id,
        handler: async function (response: any) {
          try {
            // 4) VERIFY PAYMENT
            const verifyRes = await fetch(API_URLS.PAYMENTS.VERIFY_PAYMENT, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(response),
            });
            if (!verifyRes.ok) {
              setPaymentError("Payment verification failed. If you were charged, please contact support.");
              return;
            }
            const verifyData = await verifyRes.json();
            if (verifyData.status !== "success") {
              setPaymentError("Payment verification failed. Please retry.");
              return;
            }
            if (appliedCoupon?.code) {
              fetch(API_URLS.COUPONS.USE, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code: appliedCoupon.code }),
              }).catch(() => {});
            }
            const paymentId = verifyData.payment_id || verifyData.razorpay_payment_id;

            // 5) BOOK
            const hotelRoomsDetails = buildHotelRoomsDetails(guestsForSave, paxRooms);
            const bookingCode =
              prebookData?.HotelResult?.[0]?.BookingCode ||
              prebookData?.BookingCode ||
              null;

            const bookingPayload: any = {
              PaymentId: paymentId,
              TempId: tempId,
              HotelRoomsDetails: hotelRoomsDetails,
              BookingCode: bookingCode,
              GuestNationality: paxRooms?.[0]?.GuestNationality || location.state?.GuestNationality || "IN",
              IsVoucherBooking: true,
              NetAmount: netAmount,
            };

            setBookingInProgress(true);
            setBookingMessage("Finalising booking... this may take a moment.");

            try {
              const bookingRes = await fetch(API_URLS.HOTELS.BOOK, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(bookingPayload),
              });

              if (!bookingRes.ok) {
                let msg = "Booking failed after payment. Please contact support.";
                try {
                  const j = await bookingRes.json();
                  msg = j?.error || j?.message || msg;
                } catch {}
                setPaymentError(msg);
                setBookingInProgress(false);
                setBookingMessage(null);
                return;
              }

              const bookingData = await bookingRes.json();
              const bookResult = bookingData?.BookResult || bookingData?.GetBookingDetailResult || bookingData;
              const BookingId = bookResult?.BookingId || bookingData?.BookingId || null;
              const TraceId = bookResult?.TraceId || bookingData?.TraceId || null;

              setBookingInProgress(false);
              setBookingMessage(null);

              navigate(routes.hotelBooking, {
                state: {
                  savedGuestDetails: guestRes,
                  hotelDetails,
                  prebookData,
                  payment: verifyData,
                  booking: bookingData,
                  BookingId,
                  TraceId,
                },
              });
            } catch (bookErr) {
              console.error("Booking API error:", bookErr);
              setBookingInProgress(false);
              setBookingMessage(null);
              setPaymentError("Booking failed after payment. Please contact support.");
            }
          } catch (err: unknown) {
            console.error("Booking error after payment:", err);
            setBookingInProgress(false);
            setBookingMessage(null);
            setPaymentError("Booking failed after payment.");
          }
        },
        prefill: {
          name: `${lead.FirstName} ${lead.LastName}`,
          email: lead.Email,
          contact: lead.ContactNo,
        },
        theme: { color: "#3399cc" },
        modal: {
          ondismiss: () => setShowPaymentCancelledModal(true),
        },
      };

      const razor = new window.Razorpay(options);
      try {
        razor.on && razor.on("payment.failed", function () {
          setPaymentError("Payment failed. Please try another card or method.");
        });
      } catch {}
      razor.open();
    } catch (err: unknown) {
      console.error("Guest save / payment / booking failed:", err);
      const msg = (err && typeof err === "object" && "message" in err) ? (err as any).message : "Unable to complete booking. Please try again or contact support.";
      setFormErrors([msg]);
    } finally {
      setSavingGuests(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = bookingInProgress ? "hidden" : "";
  }, [bookingInProgress]);

  if (!firstRoom) {
    return (
      <div className="container py-5">
        <h4 className="text-danger">No room details found. Please go back and select a room again.</h4>
      </div>
    );
  }

  return (
    <div>
      {bookingInProgress && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1080,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(6px)",
            background: "rgba(255,255,255,0.6)",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.95)",
              padding: 24,
              borderRadius: 12,
              boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
              textAlign: "center",
              minWidth: 300,
              maxWidth: "90%",
            }}
          >
            <div className="spinner-border text-primary" role="status" style={{ width: 48, height: 48 }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <h6 style={{ marginTop: 12 }}>{bookingMessage || "Finalising booking..."}</h6>
            <p className="small text-muted" style={{ marginTop: 6 }}>
              We are confirming your booking with the hotel. Please do not refresh or close this page.
            </p>
          </div>
        </div>
      )}

      {showPaymentCancelledModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
          }}
        >
          <div style={{ background: "#fff", borderRadius: 8, padding: 20, width: 420, maxWidth: "94%" }}>
            <h5>Payment not completed</h5>
            <p className="small text-muted">
              It looks like you closed the payment window. To complete the booking, please click{" "}
              <strong>Retry Payment</strong> or cancel to return and edit guest details.
            </p>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary" onClick={() => setShowPaymentCancelledModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowPaymentCancelledModal(false);
                  handleSaveGuests();
                }}
              >
                Retry Payment
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="breadcrumb-bar flightList-banner-bg text-center">
        <div className="container">
          <h2 className="breadcrumb-title mb-2">Hotel Guest Details</h2>
        </div>
      </div>

      <div className="content">
        <div className="container">
          <div className="row">
            {/* Left: Guest forms */}
            <div className="col-xl-8">
              <div className="card shadow-none bg-light-200 mt-4">
                <div className="card-header">
                  <h5 className="mb-0">
                    <i className="isax isax-user me-2"></i>
                    Guest Details
                  </h5>
                </div>
                <div className="card-body">
                  {formErrors.length > 0 && (
                    <div className="alert alert-danger">
                      <ul className="mb-0">
                        {formErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {guests.map((guest, index) => (
                    <div key={index} className="guest-form mb-4 pb-4 border-bottom">
                      <h6 className="mb-3 text-primary">
                        {guest.IsLeadGuest ? "Lead Guest" : `Guest ${index + 1}`}
                        {guest.IsLeadGuest && <span className="badge bg-primary ms-2">Lead</span>}
                      </h6>

                      {/* Title, Names */}
                      <div className="row">
                        <div className="col-md-3 mb-3">
                          <label className="form-label">Title *</label>
                          <select
                            className="form-control"
                            value={guest.Title}
                            onChange={(e) => handleGuestChange(index, "Title", e.target.value)}
                          >
                            <option value="Mr">Mr</option>
                            <option value="Ms">Ms</option>
                            <option value="Mrs">Mrs</option>
                            <option value="Master">Master</option>
                            <option value="Miss">Miss</option>
                          </select>
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">First Name *</label>
                          <input
                            type="text"
                            className={`form-control ${formFieldErrors[`${index}.FirstName`] ? "is-invalid" : ""}`}
                            value={guest.FirstName}
                            onChange={(e) => handleGuestChange(index, "FirstName", e.target.value)}
                            onBlur={() => validateField(index, "FirstName")}
                          />
                          <div className="invalid-feedback">{formFieldErrors[`${index}.FirstName`]}</div>
                        </div>
                        <div className="col-md-5 mb-3">
                          <label className="form-label">Last Name *</label>
                          <input
                            type="text"
                            className={`form-control ${formFieldErrors[`${index}.LastName`] ? "is-invalid" : ""}`}
                            value={guest.LastName}
                            onChange={(e) => handleGuestChange(index, "LastName", e.target.value)}
                            onBlur={() => validateField(index, "LastName")}
                          />
                          <div className="invalid-feedback">{formFieldErrors[`${index}.LastName`]}</div>
                        </div>
                      </div>

                      {/* Child toggle */}
                      <div className="row">
                        <div className="col-md-3 mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!!guest.IsChild}
                              onChange={(e) => handleGuestChange(index, "IsChild", e.target.checked)}
                              id={`isChild-${index}`}
                            />
                            <label className="form-check-label" htmlFor={`isChild-${index}`}>
                              Child
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Gender, DOB */}
                      <div className="row">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Gender *</label>
                          <select
                            className="form-control"
                            value={guest.Gender}
                            onChange={(e) => handleGuestChange(index, "Gender", parseInt(e.target.value || "1"))}
                          >
                            <option value={1}>Male</option>
                            <option value={2}>Female</option>
                            <option value={3}>Other</option>
                          </select>
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Date of Birth {guest.IsChild ? "" : "*"}</label>
                          <DatePicker
                            className={`form-control w-100 ${formFieldErrors[`${index}.DateOfBirth`] ? "is-invalid" : ""}`}
                            value={guest.DateOfBirth ? dayjs(guest.DateOfBirth) : null}
                            onChange={(date) => handleGuestChange(index, "DateOfBirth", date ? date.format("YYYY-MM-DD") : "")}
                            format="YYYY-MM-DD"
                            onBlur={() => validateField(index, "DateOfBirth")}
                          />
                          <div className="invalid-feedback">{formFieldErrors[`${index}.DateOfBirth`]}</div>
                        </div>

                        {guest.IsChild && (
                          <div className="col-md-4 mb-3">
                            <label className="form-label">Age {guest.IsChild ? "*" : ""}</label>
                            <input
                              type="number"
                              min={0}
                              max={17}
                              className={`form-control ${formFieldErrors[`${index}.Age`] ? "is-invalid" : ""}`}
                              value={guest.Age ?? ""}
                              onChange={(e) => handleGuestChange(index, "Age", e.target.value === "" ? null : Number(e.target.value))}
                              onBlur={() => validateField(index, "Age")}
                            />
                            <div className="invalid-feedback">{formFieldErrors[`${index}.Age`]}</div>
                            <div className="small text-muted mt-1">You can enter DOB or Age — we'll convert as needed.</div>
                          </div>
                        )}
                      </div>

                      {/* Nationality, PAN (hide PAN for children) */}
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Nationality *</label>
                          <input
                            type="text"
                            className={`form-control ${formFieldErrors[`${index}.Nationality`] ? "is-invalid" : ""}`}
                            value={guest.Nationality}
                            onChange={(e) => handleGuestChange(index, "Nationality", e.target.value)}
                            onBlur={() => validateField(index, "Nationality")}
                          />
                          <div className="invalid-feedback">{formFieldErrors[`${index}.Nationality`]}</div>
                        </div>

                        {!guest.IsChild && (
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              PAN Number {validationInfo?.PanMandatory ? "*" : ""}
                            </label>
                            <input
                              type="text"
                              className={`form-control ${formFieldErrors[`${index}.PanCard`] ? "is-invalid" : ""}`}
                              value={guest.PanCard}
                              onChange={(e) => handleGuestChange(index, "PanCard", e.target.value)}
                              onBlur={() => validateField(index, "PanCard")}
                            />
                            <div className="invalid-feedback">{formFieldErrors[`${index}.PanCard`]}</div>
                          </div>
                        )}
                      </div>

                      {/* Passport if required */}
                      {validationInfo?.PassportMandatory && (
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Passport Number *</label>
                            <input
                              type="text"
                              className={`form-control ${formFieldErrors[`${index}.Passport`] ? "is-invalid" : ""}`}
                              value={guest.Passport}
                              onChange={(e) => handleGuestChange(index, "Passport", e.target.value)}
                              onBlur={() => validateField(index, "Passport")}
                            />
                            <div className="invalid-feedback">{formFieldErrors[`${index}.Passport`]}</div>
                          </div>
                        </div>
                      )}

                      {/* Lead Guest Extra Fields */}
                      {guest.IsLeadGuest && (
                        <>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Contact Number *</label>
                              <input
                                type="tel"
                                className={`form-control ${formFieldErrors[`${index}.ContactNo`] ? "is-invalid" : ""}`}
                                value={guest.ContactNo}
                                onChange={(e) => handleGuestChange(index, "ContactNo", e.target.value)}
                                onBlur={() => validateField(index, "ContactNo")}
                              />
                              <div className="invalid-feedback">{formFieldErrors[`${index}.ContactNo`]}</div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Email Address *</label>
                              <input
                                type="email"
                                className={`form-control ${formFieldErrors[`${index}.Email`] ? "is-invalid" : ""}`}
                                value={guest.Email}
                                onChange={(e) => handleGuestChange(index, "Email", e.target.value)}
                                onBlur={() => validateField(index, "Email")}
                              />
                              <div className="invalid-feedback">{formFieldErrors[`${index}.Email`]}</div>
                            </div>
                          </div>

                          {validationInfo?.GSTAllowed && (
                            <div className="row">
                              <div className="col-md-12 mb-3">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={gstEnabled}
                                    onChange={(e) => setGstEnabled(e.target.checked)}
                                    id="gstCheck"
                                  />
                                  <label className="form-check-label" htmlFor="gstCheck">
                                    Add GST Details
                                  </label>
                                </div>
                              </div>
                              {gstEnabled && (
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">GST Number *</label>
                                  <input
                                    type="text"
                                    className={`form-control ${!isValidGSTIN(gstNumber) && gstNumber ? "is-invalid" : ""}`}
                                    value={gstNumber}
                                    onChange={(e) => setGstNumber(e.target.value)}
                                    onBlur={() => {
                                      if (!isValidGSTIN(gstNumber)) setFormFieldErrors((p) => ({ ...p, gstNumber: "Invalid GSTIN" }));
                                      else setFormFieldErrors((p) => { const c = { ...p }; delete c.gstNumber; return c; });
                                    }}
                                  />
                                  <div className="invalid-feedback">{formFieldErrors.gstNumber}</div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {paymentError && <div className="alert alert-danger">{paymentError}</div>}

                  <div className="text-end">
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={handleSaveGuests}
                      disabled={savingGuests || bookingInProgress}
                    >
                      {savingGuests ? "Processing..." : "Review & Continue"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Booking Review */}
            <div className="col-xl-4">
              <div className="card shadow-sm sticky-top">
                <div className="card-body">
                  <h5 className="mb-2">{hotelDetails.HotelName}</h5>
                  <p className="text-muted">{hotelDetails.Address}</p>
                  <p>
                    📍{" "}
                    <a href={`https://www.google.com/maps?q=${hotelDetails.Map}`} target="_blank" rel="noreferrer">
                      See Location
                    </a>
                  </p>

                  {/* Offers button (opens offer popup) */}
                  <div className="mb-3">
                    <button className="btn btn-outline-secondary w-100 mb-2" onClick={() => setOffersVisible(true)}>
                      View Offers
                    </button>
                  </div>

                  {/* Room Info */}
                  <h6 className="mt-4">Selected Room</h6>
                  <p className="fw-bold">
                    {Array.isArray(firstRoom?.Name) ? firstRoom?.Name.join(", ") : firstRoom?.Name}
                  </p>
                  <div className="mb-2">
                    <span className="badge bg-info me-2">{firstRoom?.MealType}</span>
                    {firstRoom?.Inclusion && <span className="badge bg-light me-2">{firstRoom.Inclusion}</span>}
                    {firstRoom?.RoomPromotion?.map((promo: any, i: number) => (
                      <span key={i} className="badge bg-warning text-dark me-2">
                        {promo}
                      </span>
                    ))}
                    {firstRoom?.IsRefundable ? (
                      <span className="badge bg-success">Refundable</span>
                    ) : (
                      <span className="badge bg-danger">Non-Refundable</span>
                    )}
                  </div>

                  {/* Cancellation Policy */}
                  {firstRoom?.CancelPolicies?.length > 0 && (
                    <div className="mb-3">
                      <h6 className="small fw-bold">Cancellation Policy</h6>
                      <ul className="list-unstyled small">
                        {firstRoom.CancelPolicies.map((p: any, i: number) => (
                          <li key={i}>
                            📅 {p.FromDate} →{" "}
                            <span className="badge bg-secondary">
                              {p.ChargeType === "Percentage"
                                ? `${p.CancellationCharge}%`
                                : `${p.ChargeType} - ${p.CancellationCharge}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Rate Conditions */}
                  {prebookData?.HotelResult?.[0]?.RateConditions?.length > 0 && (
                    <div className="mb-3">
                      <h6 className="small fw-bold">Rate Conditions</h6>
                      <div
                        className="border rounded p-2 small bg-white"
                        style={{ maxHeight: "120px", overflowY: "auto" }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            prebookData.HotelResult[0].RateConditions.join("<br/>")
                          ),
                        }}
                      />
                    </div>
                  )}

                  {/* Fare Breakdown */}
                  <h6 className="mt-4">Fare Breakdown</h6>
                  <ul className="list-unstyled small">
                    {firstRoom?.PriceBreakUp?.map((item: any, idx: number) => (
                      <li key={idx}>
                        Room Rate: {item.RoomRate} {prebookData?.HotelResult?.[0]?.Currency || ""}
                        {item.RoomTax && ` + Tax: ${item.RoomTax}`}
                      </li>
                    ))}
                  </ul>
                  <h5 className="text-primary mt-2">
                    Total: {firstRoom?.TotalFare} {prebookData?.HotelResult?.[0]?.Currency || ""}
                  </h5>
                </div>
              </div>
            </div>
            {/* /Right */}
          </div>
        </div>
      </div>

      {/* Offer popup (demo-only offers; clicking Pay Now triggers the same flow) */}
      <OfferPopup
        visible={offersVisible}
        onClose={() => setOffersVisible(false)}
        onPayNow={() => handleSaveGuests()}
        isHotelOffers={true}
        bookingAmount={
          prebookData?.HotelResult?.[0]?.Rooms?.[0]?.NetAmount ||
          prebookData?.HotelResult?.[0]?.Rooms?.[0]?.TotalFare ||
          0
        }
        appliedCoupon={appliedCoupon}
        onCouponApplied={setAppliedCoupon}
      />
    </div>
  );
};

export default HotelGuestDetails;
